import React, { useContext, useMemo, useState, useEffect } from 'react';
import { Box, Paper, Typography, Chip, IconButton, Stack } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DateTime } from 'luxon';
import { AuthContext, MyContext } from '../context/AuthContext';
import { CacheContext, CacheContextType } from '../context/CacheContext';
import { hapticTap } from '../lib/haptics';

/**
 * GUIDED FIRST POST
 *
 * The first-90-seconds experience for brand-new signups. Detects users
 * whose `join_date` is within the past 10 minutes AND who haven't made
 * any post yet, then shows a warm welcome banner above the composer
 * with three tap-to-pre-fill prompt chips.
 *
 * The chips dispatch a `wipe-composer-prefill` window event that the
 * SubmitPostComponent listens for and uses to seed the textarea. This
 * keeps the coupling loose — the banner doesn't need a ref to the
 * composer, and the composer doesn't need to know about onboarding.
 *
 * Once the user either dismisses the banner or posts, it stays hidden
 * (tracked via localStorage so it doesn't flicker on re-render).
 *
 * This is the single highest-leverage place in the funnel: the
 * difference between a signed-up user who posts in 30s vs. bounces.
 */

const DISMISS_KEY = 'wipe_guided_first_post_dismissed';
const NEW_USER_WINDOW_MINUTES = 10;

const PROMPTS = [
  {
    label: '👋 say hi',
    text: "hi wipe.haus! i just joined and i'm ready to watch my words evaporate in real-time.",
  },
  {
    label: '💭 a fleeting thought',
    text: "here's a thought i don't want to remember forever: ",
  },
  {
    label: '🔥 hot take',
    text: "hot take, alive for 24h only: ",
  },
  {
    label: '☕ right now',
    text: "right now, at this exact moment: ",
  },
];

function GuidedFirstPost() {
  const userRecord = useContext<MyContext>(AuthContext).userRecord;
  const { allPosts } = useContext<CacheContextType>(CacheContext);
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });

  const isNewUser = useMemo(() => {
    if (!userRecord) return false;
    const joined = DateTime.fromISO(userRecord.join_date);
    if (!joined.isValid) return false;
    return DateTime.now().diff(joined, 'minutes').minutes < NEW_USER_WINDOW_MINUTES;
  }, [userRecord]);

  const hasPosted = useMemo(() => {
    if (!userRecord) return true;
    return allPosts().some((p) => p.user_record_id === userRecord.id);
  }, [userRecord, allPosts]);

  // Auto-dismiss forever once they post their first thing.
  useEffect(() => {
    if (hasPosted && !dismissed) {
      try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
      setDismissed(true);
    }
  }, [hasPosted, dismissed]);

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
    setDismissed(true);
  };

  const handlePick = (text: string) => {
    hapticTap();
    window.dispatchEvent(new CustomEvent('wipe-composer-prefill', { detail: { text } }));
    // Nudge focus to the composer textarea after the prefill lands.
    requestAnimationFrame(() => {
      const ta = document.querySelector<HTMLTextAreaElement>('textarea');
      if (ta) {
        ta.focus();
        // Put caret at the end so users can keep typing.
        const v = ta.value;
        ta.setSelectionRange(v.length, v.length);
      }
    });
  };

  if (!userRecord || !isNewUser || hasPosted || dismissed) return null;

  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        mb: 2,
        p: 1.75,
        borderRadius: 0,
        border: '2px solid currentColor',
        position: 'relative',
        background: 'transparent',
      }}
    >
      <IconButton
        size="small"
        onClick={handleDismiss}
        sx={{ position: 'absolute', top: 6, right: 6, opacity: 0.6, '&:hover': { opacity: 1 } }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ pr: 3 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            bgcolor: 'text.primary',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            animation: 'wave 2s ease-in-out infinite',
            '@keyframes wave': {
              '0%, 100%': { transform: 'rotate(0deg)' },
              '25%': { transform: 'rotate(-10deg)' },
              '75%': { transform: 'rotate(10deg)' },
            },
          }}
        >
          <Box component="span" sx={{ fontSize: 20, lineHeight: 1 }} role="img" aria-label="wave">👋</Box>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: '"VT323", monospace',
              fontSize: '1.15rem',
              letterSpacing: 1,
              color: 'text.primary',
              fontWeight: 700,
              lineHeight: 1.1,
              mb: 0.25,
            }}
          >
            WELCOME, @{userRecord.username.toUpperCase()}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25, fontSize: '0.85rem' }}>
            Your first post lasts 24 hours. Tap a prompt to start writing.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {PROMPTS.map((p) => (
              <Chip
                key={p.label}
                label={p.label}
                size="small"
                clickable
                onClick={() => handlePick(p.text)}
                sx={{
                  fontSize: '0.78rem',
                  height: 26,
                  bgcolor: 'transparent',
                  color: 'text.primary',
                  border: '1px solid',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: 'text.primary',
                    color: 'white',
                    borderColor: 'text.primary',
                  },
                  transition: 'all 0.15s ease',
                }}
              />
            ))}
          </Box>
        </Box>
      </Stack>
    </Paper>
  );
}

export default GuidedFirstPost;
